import db from "./offlineDb";
import apiTamboUrl from "./api";
import { TAMBO_LITE_MODE } from "@/config/liteBeta";

const SYNC_KEY = "tambopal_last_sync_at";

/**
 * Mapeo de tablas del delta sync a tablas de Dexie.
 * El backend devuelve nombres como "animales", "corrales_feedlot", etc.
 */
const TABLE_MAP = {
  animales: "animales",
  lotes: "lotes",
  inseminaciones: "inseminaciones",
  tactos: "tactos",
  inventario_veterinario: "inventario_veterinario",
  inventario_siembra: "inventario_siembra",
  corrales_feedlot: "corrales_feedlot",
  corrales_asignaciones: "corrales_asignaciones",
  registros_peso: "registros_peso",
  enfermedades_feedlot: "enfermedades_feedlot",
  recorridos_corral: "recorridos_corral",
};

/**
 * Sincronización delta: descarga del backend solo los registros modificados
 * desde la última sincronización y los guarda en Dexie.
 *
 * @param {string[]|null} tablas - Tablas a sincronizar, null para todas.
 * @returns {boolean} true si la sincronización fue exitosa.
 */
export async function syncDelta(tablas = null) {
  if (TAMBO_LITE_MODE) return false;

  const since = localStorage.getItem(SYNC_KEY) || null;
  const params = {};
  if (since) params.since = since;
  if (tablas) params.tablas = tablas.join(",");

  try {
    const { data } = await apiTamboUrl.get("/sync/delta", { params });

    for (const [tabla, registros] of Object.entries(data.datos || {})) {
      const dexieTable = TABLE_MAP[tabla];
      if (dexieTable && db[dexieTable] && registros.length > 0) {
        let items = registros;
        if (dexieTable === "lotes") {
          items = registros.map((r) => ({
            ...r,
            id: r.lote_id || r.id,
            nombre: r.nombre_lote || r.nombre,
          }));
        }
        await db[dexieTable].bulkPut(items);
      }
    }

    // Guardar timestamp del servidor para el próximo delta
    if (data.hasta) {
      localStorage.setItem(SYNC_KEY, data.hasta);
    }

    console.log(`✅ Sync delta completado: ${data.total_registros} registros`);
    return true;
  } catch (err) {
    console.warn("⚠️ Sync delta falló (sin red o error):", err.message);
    return false;
  }
}

/**
 * Sincronización inicial: descarga completa sin parámetro `since`.
 * Limpia las tablas locales antes de insertar.
 */
export async function syncFull(tablas = null) {
  if (TAMBO_LITE_MODE) return false;

  // Limpiar timestamp para forzar descarga completa
  localStorage.removeItem(SYNC_KEY);

  const params = {};
  if (tablas) params.tablas = tablas.join(",");

  try {
    const { data } = await apiTamboUrl.get("/sync/delta", { params });

    for (const [tabla, registros] of Object.entries(data.datos || {})) {
      const dexieTable = TABLE_MAP[tabla];
      if (dexieTable && db[dexieTable]) {
        await db[dexieTable].clear();
        if (registros.length > 0) {
          let items = registros;
          if (dexieTable === "lotes") {
            items = registros.map((r) => ({
              ...r,
              id: r.lote_id || r.id,
              nombre: r.nombre_lote || r.nombre,
            }));
          }
          await db[dexieTable].bulkPut(items);
        }
      }
    }

    if (data.hasta) {
      localStorage.setItem(SYNC_KEY, data.hasta);
    }

    console.log(`✅ Sync completo: ${data.total_registros} registros`);
    return true;
  } catch (err) {
    console.warn("⚠️ Sync completo falló:", err.message);
    return false;
  }
}

/**
 * Devuelve la cantidad de operaciones pendientes de sincronizar.
 */
export async function getPendingOpsCount() {
  return await db.pendingOps.where("synced").equals(0).count();
}

/**
 * Limpia datos locales de Dexie y el timestamp de sync.
 * Usar al hacer logout o cambiar de tambo.
 */
export async function clearOfflineData() {
  const tables = Object.values(TABLE_MAP);
  for (const t of tables) {
    if (db[t]) await db[t].clear();
  }
  await db.pendingOps.clear();
  localStorage.removeItem(SYNC_KEY);
  console.log("🧹 Datos offline limpiados");
}

/**
 * Limpia operaciones pendientes ya sincronizadas (synced=1) con más de 7 días.
 */
export async function cleanOldSyncedOps() {
  const cutoff = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  const old = await db.pendingOps
    .where("synced")
    .equals(1)
    .filter((op) => op.createdAt < cutoff)
    .toArray();

  if (old.length > 0) {
    await db.pendingOps.bulkDelete(old.map((o) => o.id));
    console.log(`🧹 Limpiados ${old.length} ops sincronizadas antiguas`);
  }
}
